/**
 * USDZ loader pipeline — loads Apple USDZ files with PBR materials and textures.
 *
 * Uses Three.js USDZLoader and returns a normalized ModelResult.
 */

import type * as THREE from 'three/webgpu';
import { USDZLoader } from 'three/addons/loaders/USDZLoader.js';
import { Logger } from '../core/Logger.js';
import type { ModelResult, LoadOptions } from './ModelPipeline.js';

/* ------------------------------------------------------------------ */
/*  USDZPipeline                                                      */
/* ------------------------------------------------------------------ */

export class USDZPipeline {
  private loader: InstanceType<typeof USDZLoader>;

  constructor() {
    this.loader = new USDZLoader();
    Logger.info('Assets', 'USDZ pipeline created');
  }

  /**
   * Load a USDZ file and return a normalized ModelResult.
   *
   * The Three.js USDZLoader does not extract cameras or animations,
   * so those fields are always empty arrays.
   */
  async load(url: string, options: LoadOptions = {}): Promise<ModelResult> {
    const { onProgress } = options;

    return new Promise<ModelResult>((resolve, reject) => {
      (this.loader as unknown as { load: (
        url: string,
        onLoad: (group: THREE.Group) => void,
        onProgress?: (event: ProgressEvent) => void,
        onError?: (error: unknown) => void,
      ) => void }).load(
        url,
        (group: THREE.Group) => {
          Logger.debug('Assets', `Loaded USDZ: ${url}`);
          resolve({
            scene: group,
            scenes: [group],
            cameras: [],
            animations: [],
            format: 'usdz',
            meta: { url },
          });
        },
        onProgress,
        (error: unknown) => {
          Logger.error('Assets', `Failed to load USDZ: ${url}`, error);
          reject(error instanceof Error ? error : new Error(String(error)));
        },
      );
    });
  }
}
